import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';

import { DASHBOARD_AUTH_BASE } from './dashboardAuth';

// Public (unauthenticated) staff set-password landing page. The link (purpose
// `dashboard_password_reset`) is issued from the ops console
// (POST /v2/console/users/<id>/send_password_reset/), by a new-account invite, or by the
// sign-in's "Forgot password?", and points here. The preview's `mode` tells an invite
// ("Set your password", a welcome) from a reset; both are redeemed the same way. Talks to
// the backend's public /v2/dashboard_auth/ endpoints — no Auth0, no token.

function getCode() {
    return new URLSearchParams(window.location.search).get('code') || '';
}

export default function PasswordResetPage() {
    const code = getCode();
    const [preview, setPreview] = useState(null); // { email, first_name, expires_ts, mode }
    const [previewError, setPreviewError] = useState(null);
    const [loading, setLoading] = useState(true);

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);
    const [done, setDone] = useState(false);
    const invite = !!preview && preview.mode === 'invite';

    useEffect(() => {
        if (!code) {
            setPreviewError('This reset link is missing its code.');
            setLoading(false);
            return;
        }
        fetch(`${DASHBOARD_AUTH_BASE}/password_reset/preview/?code=${encodeURIComponent(code)}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((data) => setPreview(data))
            .catch(() => setPreviewError('This reset link is invalid or has expired.'))
            .finally(() => setLoading(false));
    }, [code]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErrors([]);
        if (password !== confirm) {
            setErrors(['Passwords do not match.']);
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${DASHBOARD_AUTH_BASE}/password_reset/confirm/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, new_password: password }),
            });
            if (res.ok) {
                setDone(true);
                return;
            }
            const body = await res.json().catch(() => ({}));
            setErrors(body.errors && body.errors.length ? body.errors : [body.detail || 'Could not reset password.']);
        } catch (_e) {
            setErrors(['Network error. Please try again.']);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
            <Paper sx={{ p: 4, width: '100%', maxWidth: 420 }}>
                <Typography variant="h5" gutterBottom>
                    {invite ? 'Set your password' : 'Reset your password'}
                </Typography>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && previewError && <Alert severity="error">{previewError}</Alert>}

                {!loading && !previewError && done && (
                    <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {invite
                                ? 'Your password is set. You can sign in now.'
                                : 'Your password has been reset. You have been signed out of all devices.'}
                        </Alert>
                        <Button href="/" variant="contained" fullWidth>
                            Go to sign in
                        </Button>
                    </Box>
                )}

                {!loading && !previewError && !done && preview && (
                    <Box component="form" onSubmit={onSubmit}>
                        {invite ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {preview.first_name ? `Welcome, ${preview.first_name}!` : 'Welcome!'} Choose a password
                                for <strong>{preview.email}</strong> to sign in to your dashboard.
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Setting a new password for <strong>{preview.email}</strong>.
                            </Typography>
                        )}
                        <TextField
                            type="password"
                            label="New password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            margin="normal"
                            autoComplete="new-password"
                            autoFocus
                        />
                        <TextField
                            type="password"
                            label="Confirm password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            fullWidth
                            margin="normal"
                            autoComplete="new-password"
                        />
                        {errors.length > 0 && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {errors.map((m) => (
                                        <li key={m}>{m}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{ mt: 2 }}
                            disabled={submitting || !password || !confirm}
                        >
                            {invite
                                ? (submitting ? 'Setting…' : 'Set password')
                                : (submitting ? 'Resetting…' : 'Reset password')}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
